/**
 * All Stripe calls go through /api/stripe/* — a Cloudflare Worker in production,
 * proxied by Vite dev server to the worker running locally.
 *
 * Never expose the secret key in the browser bundle.
 */

const BASE = '/api/stripe'

async function get(path) {
  const res = await fetch(`${BASE}${path}`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || `Stripe API error ${res.status}`)
  }
  return res.json()
}

/** Pull all active subscriptions — paginated, returns full list */
export async function getActiveSubscriptions() {
  return get('/subscriptions/active')
}

/** Pull charges received this calendar month */
export async function getChargesThisMonth() {
  return get('/charges/this-month')
}

/** Pull payment intent / charge summary (today / week / month) */
export async function getRevenueSummary() {
  return get('/revenue/summary')
}

/** Pull all active subscriptions with reconciliation data */
export async function getPaymentPlanReconciliation() {
  return get('/reconciliation/payment-plans')
}

/** Pull all refunds */
export async function getRefunds(period = 'month') {
  return get(`/refunds?period=${period}`)
}

/** Fetch payment history for a single plan row (called on expand) */
export async function getPlanHistory({ subId, custId, amount, since }) {
  const params = new URLSearchParams()
  if (subId)  params.set('subId',  subId)
  if (custId) params.set('custId', custId)
  if (amount) params.set('amount', amount)
  if (since)  params.set('since',  since)
  return get(`/reconciliation/history?${params}`)
}

/** Mark a failed plan as lost income (POST) */
export async function markLostIncome(payload) {
  const res = await fetch(`${BASE}/lost-income`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(`Failed to save lost income: ${res.status}`)
  return res.json()
}

/** Pull all manually-logged lost income records */
export async function getLostIncomeRecords() {
  return get('/lost-income')
}

/** Pull all manually-toggled Active/Paused/Lost plan states */
export async function getPlanStates() {
  return get('/plan-state')
}

/** Set the Active/Paused/Lost state for a single plan (POST) */
export async function setPlanState(planId, status) {
  const res = await fetch(`${BASE}/plan-state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ planId, status }),
  })
  if (!res.ok) throw new Error(`Failed to save plan state: ${res.status}`)
  return res.json()
}
